import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from "@angular/common/http";
import { inject } from "@angular/core";
import { catchError, Observable, switchMap, throwError } from "rxjs";
import { AppStorageService } from "shared-service/app-storage/app.storage";
import {
    AuthService,
    SessionRefreshUnavailableError,
} from "shared-service/services/auth/auth.service";
import {
    BUSINESS_WORKSPACE_TENANT_HEADER,
} from "shared-service/services/tenancy/workspace-tenant.constants";
import { AUTHORIZATION_DENIED_EVENT } from "shared-service/services/grpc/grpc-auth.interceptor";
import { HTTP_WORKSPACE_TENANT_ID } from "shared-service/services/tenancy/workspace-tenant-http-context";

/**
 * BFF (Authorization Code + PKCE): KHÔNG còn gắn Bearer token — xác thực đi bằng cookie httpOnly (BE đổi
 * cookie→Bearer server-side). Interceptor bật `withCredentials` để cookie đi kèm mọi REST same-origin,
 * gắn context X-System-Id, và trên 401 (phiên hết hạn) đưa về luồng BFF login (redirect Keycloak).
 */
export const authInterceptor = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
    const storageService = inject(AppStorageService);
    const authService = inject(AuthService);

    // Gửi kèm cookie session httpOnly + context system-id cho REST same-origin.
    let headers = req.headers.set("X-System-Id", storageService.systemId.toString());
    const workspaceTenantId = req.context.get(HTTP_WORKSPACE_TENANT_ID) ?? storageService.workspaceTenantId;
    if (Number.isSafeInteger(workspaceTenantId) && workspaceTenantId > 0) {
        headers = headers.set(BUSINESS_WORKSPACE_TENANT_HEADER, String(workspaceTenantId));
    } else {
        headers = headers.delete(BUSINESS_WORKSPACE_TENANT_HEADER);
    }

    const newReq = req.clone({
        withCredentials: true,
        headers,
    });

    const isAuthEndpoint = req.url.includes("/auth/");
    const send = (): Observable<HttpEvent<unknown>> =>
        next(newReq).pipe(
            catchError((error) => {
                // Mọi REST call 401 dùng chung đúng một lần /auth/session. BFF sẽ refresh server-side;
                // request ban đầu được retry một lần và chỉ chuyển login nếu refresh/retry vẫn thất bại.
                if (error instanceof HttpErrorResponse && error.status === 401 && !isAuthEndpoint) {
                    const returnUrl = authService.currentReturnUrl();
                    return authService.refreshToken().pipe(
                        // Catch only the forced BFF session check. Placing catchError after switchMap also
                        // catches a second downstream 401 and wrongly turns a service-specific auth/config
                        // failure into a global browser logout even though /auth/session just succeeded.
                        catchError((refreshError) => {
                            if (!(refreshError instanceof SessionRefreshUnavailableError)) {
                                authService.redirectToLogin(returnUrl);
                            }
                            return throwError(() => refreshError);
                        }),
                        switchMap(() => next(newReq)),
                    );
                }

                if (error instanceof HttpErrorResponse && error.status === 403 && typeof window !== "undefined") {
                    window.dispatchEvent(new Event(AUTHORIZATION_DENIED_EVENT));
                }

                return throwError(() => error);
            }),
        );

    if (isAuthEndpoint) {
        return send();
    }

    // Usually resolves synchronously from the freshness cache. After a long idle period it refreshes
    // the BFF session before the business request, avoiding the slower 401 -> refresh -> retry path.
    return authService.ensureSessionFresh().pipe(switchMap(() => send()));
};
